import type { KeyboardEvent, MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import type { CommissionLine } from '../../shared/api/generated/model'
import {
  issueLabels,
  money,
  participationLabels,
  percent,
  shortDate,
  statusMeta,
  triggerLabels,
} from './helpers'

export const lineKey = (r: CommissionLine, i?: number) =>
  r.id != null
    ? `id-${r.id}`
    : `${r.surgery_id}-${r.trigger}-${r.participation ?? 'na'}-${r.seller_user_id ?? 'na'}${
        i == null ? '' : `-${i}`
      }`

/**
 * Tabela densa, uma linha por parcela. A linha inteira abre o detalhe
 * (`onOpen`); links e checkbox não propagam o clique. Em seções dedicadas
 * (`context` venda/operação) o tipo da parcela não é repetido na linha.
 */
export function CommissionTable({
  rows,
  context = 'mixed',
  onOpen,
  activeKey,
  selected,
  onSelect,
  onSelectAll,
  emptyTitle = 'Nenhum lançamento neste período.',
  emptyHint = 'Os itens aparecerão aqui após a apuração.',
}: {
  rows: CommissionLine[]
  context?: 'sale' | 'operated' | 'mixed'
  onOpen?: (row: CommissionLine) => void
  activeKey?: string | null
  selected?: Set<number>
  onSelect?: (id: number) => void
  onSelectAll?: (ids: number[], checked: boolean) => void
  emptyTitle?: string
  emptyHint?: string
}) {
  if (!rows.length)
    return (
      <div className="cc-empty">
        <p>{emptyTitle}</p>
        <span>{emptyHint}</span>
      </div>
    )
  const ids = [...new Set(rows.map((r) => r.surgery_id))]
  const allSelected = !!selected && ids.every((id) => selected.has(id))
  const someSelected = !!selected && !allSelected && ids.some((id) => selected.has(id))
  const stop = (e: MouseEvent) => e.stopPropagation()
  return (
    <div className="cc-table-wrap">
      <table className={`cc-table${onOpen ? ' cc-table-clickable' : ''}`}>
        <thead>
          <tr>
            {onSelect && (
              <th className="cc-col-select">
                {onSelectAll ? (
                  <input
                    type="checkbox"
                    aria-label="Selecionar todas as cirurgias listadas"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected
                    }}
                    onChange={(e) => onSelectAll(ids, e.target.checked)}
                  />
                ) : (
                  <span className="cc-sr-only">Selecionar</span>
                )}
              </th>
            )}
            <th>Paciente</th>
            <th>Comercial</th>
            <th>Data</th>
            <th className="cc-num">Honorários</th>
            <th className="cc-num">Taxa</th>
            <th className="cc-num">Comissão</th>
            <th>Situação</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const key = lineKey(r, i)
            const status = statusMeta[r.status] ?? { label: r.status, tone: 'neutral' as const }
            const isPending = r.status === 'pending'
            const issues = (r.issues ?? []).map((k) => issueLabels[k] ?? k)
            const isSelected = selected?.has(r.surgery_id) ?? false
            const role =
              r.participation && r.participation !== 'solo'
                ? participationLabels[r.participation]
                : null
            const open = onOpen ? () => onOpen(r) : undefined
            const onKey = (e: KeyboardEvent<HTMLTableRowElement>) => {
              if (!open || e.target !== e.currentTarget) return
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                open()
              }
            }
            return (
              <tr
                key={key}
                className={[isSelected ? 'is-selected' : '', activeKey === key ? 'is-active' : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={open}
                onKeyDown={onKey}
                tabIndex={open ? 0 : undefined}
                aria-label={open ? `Abrir detalhe de ${r.patient_name}` : undefined}
              >
                {onSelect && (
                  <td className="cc-col-select" onClick={stop}>
                    <input
                      type="checkbox"
                      aria-label={`Selecionar cirurgia ${r.surgery_id}`}
                      checked={isSelected}
                      onChange={() => onSelect(r.surgery_id)}
                    />
                  </td>
                )}
                <td className="cc-cell-patient">
                  <span className="cc-primary">{r.patient_name}</span>
                  <Link
                    className="cc-ref"
                    to={`/cirurgias/${r.surgery_id}`}
                    onClick={stop}
                    title={`Abrir cirurgia #${r.surgery_id}`}
                  >
                    #{r.surgery_id}
                  </Link>
                </td>
                <td>
                  {r.seller_name ? (
                    <>
                      <span className="cc-primary">{r.seller_name}</span>
                      {role && <span className="cc-dim"> · {role}</span>}
                    </>
                  ) : (
                    <span className="cc-undefined">A definir</span>
                  )}
                </td>
                <td className="cc-cell-date">
                  {shortDate(r.source_date)}
                  {context === 'mixed' && (
                    <span className="cc-dim"> · {triggerLabels[r.trigger] ?? r.trigger}</span>
                  )}
                </td>
                <td className="cc-num">{money(r.medical_fees_base_cents)}</td>
                <td className="cc-num">{percent(r.rate_bps)}</td>
                <td className={`cc-num cc-amount${isPending ? ' is-pending' : ''}`}>
                  {money(r.commission_amount_cents)}
                </td>
                <td>
                  <span
                    className={`cc-status is-${status.tone}`}
                    title={isPending && issues.length ? issues.join(' · ') : undefined}
                  >
                    {isPending && issues.length ? issues[0] : status.label}
                    {isPending && issues.length > 1 && (
                      <span className="cc-dim"> +{issues.length - 1}</span>
                    )}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
